// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styles from './ProcessingMasterTable.module.scss';
import { mergeClasses } from 'util/commons';
import {
  MaterialType,
  ProcessingMasterDetail,
  ProcessingMasterParams,
  UpdateRateType,
  VisibleColumn,
  WasteUnit,
} from 'util/processingMasterType';
import { IconClose2 } from 'components/atoms/icons/IconClose2';
import EditableTableCell, {
  EditableTableCellValue,
} from 'components/atoms/EditableTableCell';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from 'components/molecules/SelectFieldMultipleChoise';
import { REGEX_OBJECT } from 'util/ConstantValues';

export interface FilterObject {
  inputSearch?: string;
  operatorType: string;
}
interface Props {
  displayWasteUnits: WasteUnit[];
  filter: OptionItem[];
  visibleColumns: VisibleColumn[];
  editing: boolean;
  remainingOptions: MaterialType[];
  processingMasterForm: ProcessingMasterParams;
  initRateItem: (
    item: WasteUnit,
    type?: MaterialType
  ) => ProcessingMasterDetail;
  updateRateInput: (
    action?: UpdateRateType,
    materialType?: MaterialType,
    value?: string
  ) => void;
  setFilter: Dispatch<SetStateAction<OptionItem[]>>;
  setVisibleColumns: Dispatch<SetStateAction<VisibleColumn[]>>;
  handleChangeInput: (params) => void;
  handleDeleteColumn: (visibleColumn: VisibleColumn) => void;
  errors?: Record<string, string>;
}

const ProcessingMasterTable: React.FC<Props> = ({
  displayWasteUnits,
  filter,
  visibleColumns,
  editing,
  remainingOptions,
  processingMasterForm,
  initRateItem,
  updateRateInput,
  setFilter,
  setVisibleColumns,
  handleChangeInput,
  handleDeleteColumn,
  errors,
}) => {
  const { t } = useTranslation();

  const remainingRate = (item: WasteUnit) => {
    const totalEditableRate = item.processingMasterDetails.reduce(
      (sum, item) => {
        return sum + parseFloat(item.rate || '0');
      },
      0
    );

    return parseFloat((100 - totalEditableRate).toFixed(1));
  };

  const getTypeOptions = (item: VisibleColumn) => {
    const options: SelectOption[] = remainingOptions.map(({ id, name }) => ({
      value: id,
      label: name,
    }));

    return [{ value: item.id, label: item.name }, ...options];
  };

  const handleSelectType = (
    currentColumn: VisibleColumn,
    selectedId: number
  ) => {
    if (currentColumn.id === selectedId) {
      return;
    }

    const selectedType = remainingOptions.filter(
      ({ id }) => id === selectedId
    )[0];
    const newVisibleColumns = visibleColumns.map(({ id, name }) => {
      return {
        id: id === currentColumn.id ? selectedId : id,
        name: id === currentColumn.id ? selectedType.name : name,
      };
    });

    updateRateInput(UpdateRateType.DeleteColumn, currentColumn);
    setVisibleColumns(newVisibleColumns);
  };

  const handleSetColumnValue = (
    currentColumn: VisibleColumn,
    value: string
  ) => {
    if (!REGEX_OBJECT.max100With1Decimal?.test(value)) {
      return;
    }

    const newData = visibleColumns.map((item) => {
      const editItem = { ...item };

      if (editItem.id === currentColumn.id) {
        editItem['commonValue'] = value;
      }

      return editItem;
    });

    setVisibleColumns(newData);
    updateRateInput(UpdateRateType.SetColumnValue, currentColumn, value);
  };

  const handleSelectItems = (items: OptionItem[]) => {
    setFilter(items);
    setVisibleColumns((prev) =>
      prev.map((item) => ({ ...item, commonValue: undefined }))
    );
  };

  const itemNameOptions = useMemo(() => {
    return processingMasterForm.wasteUnits.map(({ id, name }) => ({
      id: id || '',
      name: name || t('processing_master.item_name_common') || '',
    }));
  }, [processingMasterForm.materialName, t]);

  useEffect(() => {
    setFilter([]);
  }, [processingMasterForm.materialName]);

  const isAllowDeleteComlumn = useMemo(() => {
    return displayWasteUnits.length === processingMasterForm.wasteUnits.length;
  }, [displayWasteUnits]);

  return (
    <div className={styles.processingMasterTableContainer}>
      <table
        className={mergeClasses(styles.processingMasterTable, {
          [styles.processingMasterExpandedTable]: editing,
        })}
      >
        <thead>
          <tr>
            <th className={styles.itemName}>
              <div className={styles.filterBox}>
                <SelectFieldMultipleChoise
                  options={itemNameOptions}
                  name="itemName"
                  searchable={true}
                  placeholder={t('processing_master.item_name')}
                  onChange={(items) => handleSelectItems(items)}
                  selectedOptions={
                    itemNameOptions.filter((obj) =>
                      filter?.some(({ id }) => id === obj.id)
                    ) || []
                  }
                  darkMode
                  allowClear
                  usePortal
                />
              </div>
            </th>
            <th className={styles.siteName}>
              {t('processing_master.site_name')}
            </th>
            {visibleColumns.length > 0 &&
              visibleColumns.map((visibleColumn, index) => {
                return (
                  <th
                    key={index}
                    className={mergeClasses(
                      styles.SmallCol,
                      styles.theadSelectable
                    )}
                  >
                    {editing ? (
                      <>
                        {visibleColumns.length > 1 && isAllowDeleteComlumn && (
                          <button
                            className={styles.deleteColumn}
                            onClick={() => handleDeleteColumn(visibleColumn)}
                          >
                            <IconClose2
                              width={12}
                              height={12}
                              fillColor="#e7e7e7"
                            />
                          </button>
                        )}

                        <Form
                          onSubmit={() => {}}
                          mutators={{
                            setValue: (
                              [field, value],
                              state,
                              { changeValue }
                            ) => {
                              changeValue(state, field, () => value);
                            },
                          }}
                        >
                          {(props) => {
                            return (
                              <form className={styles.filterForm}>
                                <SelectField
                                  name="materialType"
                                  value={
                                    props.values.materialType ||
                                    visibleColumn.id
                                  }
                                  placeholder={t('processing_master.type')}
                                  options={getTypeOptions(visibleColumn)}
                                  onChange={(value) =>
                                    handleSelectType(
                                      visibleColumn,
                                      Number(value)
                                    )
                                  }
                                  className={styles.materialTypeSelect}
                                  isDarkmode
                                  showPlaceholderOption
                                  usePortal
                                />
                              </form>
                            );
                          }}
                        </Form>
                      </>
                    ) : (
                      visibleColumn.name
                    )}
                  </th>
                );
              })}
            <th className={styles.remainingRate}>
              {t('processing_master.remaining_rate')}
            </th>
          </tr>
        </thead>

        <tbody className={styles.tbodyContent}>
          {editing && (
            <tr className={styles.setAll}>
              <td colSpan={2}>{t('processing_master.set_all')}</td>
              {visibleColumns.length > 0 &&
                visibleColumns.map((visibleColumn, index) => {
                  return (
                    <EditableTableCell
                      key={index}
                      editing={editing}
                      name={visibleColumn.name}
                      value={visibleColumn.commonValue}
                      unit="%"
                      onChange={(e) =>
                        handleSetColumnValue(visibleColumn, String(e?.value))
                      }
                      inputProps={{
                        maxLength: 4,
                      }}
                      className={styles.SmallCol}
                    />
                  );
                })}
              <EditableTableCell
                editable={false}
                name="remainingRate"
                className={styles.remainingRate}
              />
            </tr>
          )}

          {displayWasteUnits.length ? (
            displayWasteUnits.map((item, index) => {
              return (
                <tr key={index}>
                  <EditableTableCell
                    editable={false}
                    name="itemName"
                    value={
                      item.name || t('processing_master.item_name_common') || ''
                    }
                    className={styles.itemName}
                  />
                  <EditableTableCell
                    editable={false}
                    name="siteName"
                    value={item.site?.name}
                    className={styles.siteName}
                  />
                  {visibleColumns.length > 0 &&
                    visibleColumns.map((visibleColumn, colIndex) => {
                      const rateItem = initRateItem(item, visibleColumn);
                      const onChange = (params?: {
                        materialType?: MaterialType;
                        name?: string;
                        value?:
                          | EditableTableCellValue
                          | EditableTableCellValue[];
                      }) => {
                        handleChangeInput({
                          ...params,
                          wasteUnitId: item.id,
                        });
                      };

                      return (
                        <EditableTableCell
                          key={`${index}_${colIndex}`}
                          editing={editing}
                          value={rateItem.rate}
                          unit="%"
                          name={rateItem.materialType?.name}
                          onChange={(element) =>
                            onChange({
                              ...element,
                              materialType: rateItem.materialType,
                            })
                          }
                          error={
                            errors?.[`rate_${item.id}_${visibleColumn.id}`] &&
                            t(errors?.[`rate_${item.id}_${visibleColumn.id}`], {
                              field: t('processing_master.rate'),
                              maxValue: '100%',
                            })
                          }
                          inputProps={{
                            maxLength: 4,
                          }}
                          className={mergeClasses(
                            styles.SmallCol,
                            styles.theadSelectable
                          )}
                        />
                      );
                    })}
                  <EditableTableCell
                    editable={false}
                    name="remainingRate"
                    value={`${Math.max(0, remainingRate(item))}%`}
                    className={styles.remainingRate}
                  />
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={7} className={styles.noData}>
                {t('messages.M_008')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProcessingMasterTable;
